import { Avatar, Checkbox, Divider } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useState, useEffect } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { UserOutlined } from '@ant-design/icons';

import { personColors } from '../transcript/conf';
import AudioPlayButton from './audio-play-button';

interface Props {
    croppedImages: any;
    audioBlobURL: string;
    states: any[];
    audio: React.MutableRefObject<HTMLAudioElement[]>;
    transcriptData: any;
    playAudio: (idx: number) => void;
    audiolisteners: any;
    audioPlayingIdx: number;
    frameSpeed: number;
    setAudioPlayingIdx: (idx: number) => void;
    setDoneButtonDisabled: (disabled: boolean) => void;
}

function Phase1aConfirmation(props: Props) {
    const {
        states,
        audioBlobURL,
        audio,
        croppedImages,
        transcriptData,
        audiolisteners,
        audioPlayingIdx,
        frameSpeed,
        playAudio,
        setAudioPlayingIdx,
        setDoneButtonDisabled,
    } = props;

    const [unassignedTranscriptConfirmed, setUnassignedTranscriptConfirmed] = useState(false);
    const [unassignedTagsConfirmed, setUnassignedTagsConfirmed] = useState(false);
    const [mappingConfirmed, setMappingConfirmed] = useState(false);

    useEffect(() => {
        setDoneButtonDisabled(true);
    }, []);

    let unassignedTranscriptSpeakers: string[] = [];
    let unassignedStates = [];
    let unassignedStatesIndices: number[] = [];
    const allServerIDs = states.filter((state) => state.serverID != null).map((state) => state.serverID);

    const speakerAssignments: { [speakerId: string]: string } = {};
    transcriptData.segments.forEach((segment: any) => {
        if (segment.speaker && segment.speaker.split('_')[0] == 'SPEAKER') {
            const speakerId = segment.speaker.split('_')[1];
            const assigineeId = segment.speaker.split('_')[2];

            speakerAssignments[parseInt(speakerId)] = assigineeId;
        }
    });

    Object.keys(speakerAssignments).forEach((speakerId) => {
        if (speakerAssignments[speakerId] == null || !allServerIDs.includes(parseInt(speakerAssignments[speakerId]))) {
            unassignedTranscriptSpeakers.push(speakerId);
        }
    });

    const assignedServerIDs = Object.values(speakerAssignments)
        .filter((val) => val != null)
        .map((id) => parseInt(id));
    states
        .filter((state) => state.serverID != null)
        .forEach((state, index) => {
            if (!assignedServerIDs.includes(state.serverID)) {
                unassignedStates.push(state.serverID);
                unassignedStatesIndices.push(index);
            }
        });

    if (
        (unassignedTranscriptSpeakers.length === 0 || unassignedTranscriptConfirmed) &&
        (unassignedStates.length === 0 || unassignedTagsConfirmed) &&
        mappingConfirmed
    ) {
        setDoneButtonDisabled(false);
    }

    return (
        <>
            {unassignedTranscriptSpeakers.length !== 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ paddingBottom: '16px' }}>
                        <Text strong style={{ fontSize: '16px' }}>
                            The following transcript speakers do not have any tag assigned to them:
                        </Text>
                    </div>
                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0',
                        marginBottom: '16px'
                    }}>
                        <Row gutter={[16, 16]} justify="center" align="middle">
                            {unassignedTranscriptSpeakers.map((speakerId) => (
                                <Col key={`speaker-${speakerId}`}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                        <Avatar
                                            size={48}
                                            style={{
                                                backgroundColor: personColors[parseInt(speakerId)],
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                            }}
                                            icon={<UserOutlined />}
                                        />
                                        <Text type="secondary" style={{ fontSize: '12px' }}>Speaker {speakerId}</Text>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    <div>
                        <Checkbox
                            checked={unassignedTranscriptConfirmed}
                            onChange={(e) => setUnassignedTranscriptConfirmed(e.target.checked)}
                        >
                            <Text>I confirm this is correct and as it should be</Text>
                        </Checkbox>
                    </div>
                    <Divider style={{ margin: '24px 0' }} />
                </div>
            )}
            {unassignedStatesIndices.length !== 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ paddingBottom: '16px' }}>
                        <Text strong style={{ fontSize: '16px' }}>
                            The following tags do not have any transcript speaker assigned to them:
                        </Text>
                    </div>
                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0',
                        marginBottom: '16px'
                    }}>
                        <Row gutter={[16, 16]} justify="center" align="middle">
                            {unassignedStatesIndices.map((idx) => (
                                <Col key={`state-${idx}`}>
                                    <div style={{
                                        width: '120px',
                                        height: '120px',
                                        backgroundColor: '#fff',
                                        borderRadius: '8px',
                                        border: '1px solid #e8e8e8',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
                                    }}>
                                        {states[idx].objectType === 'audioselection' && audioBlobURL ? (
                                            <AudioPlayButton
                                                audioBlobURL={audioBlobURL}
                                                audio={audio}
                                                audioPlayingIdx={audioPlayingIdx}
                                                frameSpeed={frameSpeed}
                                                audiolisteners={audiolisteners}
                                                idx={idx}
                                                playAudio={playAudio}
                                                setAudioPlayingIdx={setAudioPlayingIdx}
                                                state={states[idx]}
                                            />
                                        ) : states[idx].objectType === 'shape' && croppedImages[idx] ? (
                                            <img
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                src={croppedImages[idx]}
                                                alt={`Tag ${idx}`}
                                            />
                                        ) : (
                                            <Text type="secondary">No Preview</Text>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    <div>
                        <Checkbox
                            checked={unassignedTagsConfirmed}
                            onChange={(e) => setUnassignedTagsConfirmed(e.target.checked)}
                        >
                            <Text>I confirm this is correct and as it should be</Text>
                        </Checkbox>
                    </div>
                    <Divider style={{ margin: '24px 0' }} />
                </div>
            )}
            
            <div style={{ padding: '12px 0' }}>
                <Checkbox checked={mappingConfirmed} onChange={(e) => setMappingConfirmed(e.target.checked)}>
                    <Text strong>I confirm all tags and assignments are correct</Text>
                </Checkbox>
            </div>
        </>
    );
}

export default Phase1aConfirmation;
